
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';

const forgotPasswordSchema = z.object({
  email: z.string().email('Por favor, insira um email válido'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

const ForgotPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        toast({
          variant: "destructive",
          title: "Erro ao enviar email",
          description: error.message || "Não foi possível enviar o email de recuperação.",
        });
        return;
      }

      setEmailSent(true);
      toast({
        title: "Email enviado!",
        description: "Verifique sua caixa de entrada para redefinir sua senha.",
      });
      
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro inesperado",
        description: "Ocorreu um erro. Tente novamente.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 "
      style={{ backgroundColor: '#F1EDDF' }}
    >
      <Card className="w-full max-w-md shadow-2xl backdrop-blur-sm bg-white/90 border border-white/20">
        <CardHeader className="text-center space-y-6 pb-8">
          <div>
            <h1 
              className="text-3xl font-bold tracking-tight mb-2"
              style={{ color: '#10261F' }}
            >
              musa
            </h1>
          </div>
          <div>
            <CardTitle 
              className="text-2xl font-semibold"
              style={{ color: '#10261F' }}
            >
              {emailSent ? 'Email enviado!' : 'Recuperar senha'}
            </CardTitle>
            <CardDescription className="text-gray-600 mt-2">
              {emailSent 
                ? 'Verifique sua caixa de entrada e siga as instruções para redefinir sua senha.'
                : 'Digite seu email para receber um link de recuperação de senha'
              }
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent>
          {!emailSent ? (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700">
                        Email
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="email"
                          placeholder="seuemail@empresa.com"
                          disabled={isLoading}
                          className="h-12 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-opacity-50 transition-all duration-200"
                          style={{ 
                            focusRing: '#10261F',
                            '--tw-ring-color': '#10261F'
                          } as React.CSSProperties}
                          aria-label="Email"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full h-12 text-white font-medium rounded-lg transition-all duration-200 hover:opacity-90 focus:ring-2 focus:ring-offset-2"
                  style={{ 
                    backgroundColor: '#10261F',
                    '--tw-ring-color': '#10261F'
                  } as React.CSSProperties}
                  disabled={isLoading}
                  aria-label="Enviar link de recuperação"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Enviando...</span>
                    </div>
                  ) : (
                    'Enviar link de recuperação'
                  )}
                </Button>
              </form>
            </Form>
          ) : (
            <div className="space-y-6">
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-sm text-gray-600">
                  Se o email estiver cadastrado em nosso sistema, você receberá um link de recuperação em alguns minutos.
                </p>
              </div>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="ghost"
              className="w-full flex items-center justify-center space-x-2 text-sm hover:bg-gray-50"
              style={{ color: '#10261F' }}
              onClick={handleBackToLogin}
              disabled={isLoading}
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Voltar para o login</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
